import { MapPin, Phone, Mail, Clock, Navigation } from "lucide-react";
import { SITE } from "../data/site";
import { PageHero, Reveal, CallButton, EmailButton } from "../components/Shared";

export default function Contact() {
  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Come Make a Memory"
        sub="We'd love to see your family. Stop by, give us a call, or send Ms. Jeanette an email."
        testid="contact-hero"
      >
        <CallButton testid="contact-hero-call-btn" />
        <EmailButton testid="contact-hero-email-btn" />
      </PageHero>

      <section className="px-4 py-20 sm:px-6" data-testid="contact-info-section">
        <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-blue-100 bg-white p-8 shadow-lg shadow-blue-500/10" data-testid="contact-details-card">
              <h2 className="font-display text-2xl font-semibold">{SITE.name}</h2>
              <ul className="mt-6 space-y-5">
                <li className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-100">
                    <MapPin className="h-6 w-6 text-blue-600" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">Address</p>
                    <p className="text-base font-extrabold text-slate-800" data-testid="contact-address">{SITE.address}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-100">
                    <Phone className="h-6 w-6 text-red-500" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">Phone</p>
                    <a href={SITE.phoneHref} data-testid="contact-phone-link" className="text-base font-extrabold text-blue-600 underline focus:outline-none focus:ring-4 focus:ring-blue-300 rounded">
                      {SITE.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-yellow-100">
                    <Mail className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">Email</p>
                    <a href={SITE.emailHref} data-testid="contact-email-link" className="break-all text-base font-extrabold text-blue-600 underline focus:outline-none focus:ring-4 focus:ring-blue-300 rounded">
                      {SITE.email}
                    </a>
                  </div>
                </li>
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <CallButton testid="contact-card-call-btn" className="px-5 py-3 text-sm" />
                <EmailButton testid="contact-card-email-btn" className="px-5 py-3 text-sm" />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="flex h-full flex-col gap-6">
              <div className="rounded-3xl border border-blue-100 bg-white p-8 shadow-lg shadow-blue-500/10" data-testid="contact-hours-card">
                <h2 className="flex items-center gap-2 font-display text-2xl font-semibold">
                  <Clock className="h-6 w-6 text-blue-500" aria-hidden="true" /> Hours
                </h2>
                <ul className="mt-5 space-y-3" data-testid="contact-hours-list">
                  {SITE.hours.map((h) => (
                    <li key={h.days} className="flex items-center justify-between gap-3 rounded-2xl bg-blue-50 px-4 py-3 text-sm font-extrabold text-slate-700 sm:text-base">
                      <span>{h.days}</span>
                      <span className={h.time === "Closed" ? "text-red-500" : "text-blue-600"}>{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="overflow-hidden rounded-3xl border border-blue-100 shadow-lg shadow-blue-500/10" data-testid="contact-map-card">
                <iframe
                  title={`Map to ${SITE.name} at ${SITE.address}`}
                  src={SITE.mapsEmbed}
                  className="h-64 w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <a
                  href={SITE.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions-link"
                  className="flex items-center justify-center gap-2 bg-blue-500 px-4 py-3.5 text-sm font-extrabold text-white transition-colors hover:bg-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-300"
                >
                  <Navigation className="h-4 w-4" aria-hidden="true" />
                  Get Directions — {SITE.address}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
